import React, { useEffect, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import { onWidget, BIG_KINDS } from "@/lib/widgetBus";
import { registerMapControl } from "@/lib/mapControlBus";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { safeHref } from "@/lib/utils";
import { Mail, Calendar, FileText, Search, Newspaper, Music, MapPin, X, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";

const ICON = { mail: Mail, calendar: Calendar, document: FileText, research: Search, news: Newspaper, music: Music, map: MapPin };

/* Real interactive map (CARTO dark tiles, no API key; attribution preserved). flyTo + target pin.
   A built-in free-text search sends the query the user TYPES to OpenStreetMap/Nominatim ONLY when they press
   "Ara" (explicit consent — the provider + the exact query are shown). No autocomplete; one request per press. */
const MapWidget = ({ data, onClose }) => {
  const { t } = useI18n();
  const el = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const Lref = useRef(null);
  const didFly = useRef(false);
  const [place, setPlace] = useState(data?.place || data?.label || "");
  const [query, setQuery] = useState(data?.query || "");
  const [cands, setCands] = useState([]);
  const [busy, setBusy] = useState(false);

  const reduced = () => (typeof document !== "undefined" && document.documentElement.dataset.reduceMotion === "1")
    || (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  const goTo = (lat, lng, zoom = 16, opts = {}) => {
    const m = map.current, L = Lref.current;
    if (!m || !L) return;
    if (marker.current) marker.current.setLatLng([lat, lng]);
    else { const icon = L.divIcon({ className: "map-pin", html: "<span></span>", iconSize: [18, 18] }); marker.current = L.marker([lat, lng], { icon }).addTo(m); }
    if (reduced()) { m.setView([lat, lng], zoom); return; }
    m.flyTo([lat, lng], zoom, { duration: opts.fromWorld ? 1.8 : 1.1 });
  };

  // Bounded pan/zoom driven by a real command (Live voice transcript or a chat/voice tool action).
  // Pan moves by a fraction of the viewport; zoom is clamped to the layer's own min/max — never off-world.
  const applyCommand = (cmd) => {
    const m = map.current;
    if (!m || !cmd) return;
    const animate = !reduced();
    if (cmd.op === "zoom") {
      const z = Math.max(m.getMinZoom(), Math.min(m.getMaxZoom(), m.getZoom() + (cmd.delta || 0)));
      m.setZoom(z, { animate });
    } else if (cmd.op === "pan") {
      const size = m.getSize();
      const dx = (cmd.dx || 0) * size.x * (cmd.frac || 0.5);
      const dy = (cmd.dy || 0) * size.y * (cmd.frac || 0.5);
      m.panBy([dx, dy], { animate });
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let L; try { L = await import("leaflet"); } catch { return; }
      L = L.default || L; Lref.current = L;
      if (cancelled || !el.current || map.current) return;
      // Open on a wide WORLD view, then ease into the chosen location (temporary opener, not a permanent decor).
      // zoomControl disabled here and re-added bottom-right so it never sits under the top-left search box.
      const m = L.map(el.current, { zoomControl: false, attributionControl: true, worldCopyJump: true,
        maxBounds: [[-85, -180], [85, 180]], maxBoundsViscosity: 0.7 })
        .setView([20, 0], 2);
      L.control.zoom({ position: "bottomright" }).addTo(m);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '© OpenStreetMap contributors', subdomains: "abc", maxZoom: 19, minZoom: 2,
      }).addTo(m);
      map.current = m;
      setTimeout(() => m.invalidateSize(), 150);
      if (data?.lat != null && data?.lng != null) {
        didFly.current = true;
        setTimeout(() => goTo(Number(data.lat), Number(data.lng), data?.zoom || 15, { fromWorld: true }), reduced() ? 160 : 350);
      }
    })();
    return () => { cancelled = true; if (map.current) { map.current.remove(); map.current = null; marker.current = null; } };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Register this open map as the live pan/zoom target (Live voice + tool actions route here).
  useEffect(() => registerMapControl(applyCommand), []); // eslint-disable-line react-hooks/exhaustive-deps

  // AI emits a new location on the SAME open map → pan from current position (no jump back to the world).
  useEffect(() => {
    if (map.current && data?.lat != null && data?.lng != null) {
      goTo(Number(data.lat), Number(data.lng), data?.zoom || 15, { fromWorld: !didFly.current });
      didFly.current = true;
      setPlace(data?.place || data?.label || "");
    }
  }, [data?.lat, data?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = async () => {
    const q = (query || "").trim();
    if (!q || busy) return;
    setBusy(true); setCands([]);
    try {
      const r = await api.post("/geocode", { query: q });
      const list = r.data?.candidates || [];
      if (!list.length) { toast.error(t("map_notfound", { q })); }
      else if (list.length === 1) { const c = list[0]; didFly.current = true; goTo(c.lat, c.lng); setPlace(c.display_name); }
      else { setCands(list); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("map_fail"));
    } finally { setBusy(false); }
  };

  return (
    <div className="relative w-full h-full">
      <div ref={el} className="map-stage w-full h-full overflow-hidden" data-testid="map-stage" />

      {onClose && (
        <button onClick={onClose} data-testid="map-close" aria-label={t("close")}
          className="absolute top-2 right-2 z-[600] h-9 w-9 rounded-full flex items-center justify-center text-white/70 hover:text-white bg-[#0A1226]/80 border border-white/10 backdrop-blur">
          <X size={16} />
        </button>
      )}

      {/* minimal free-text search overlay with explicit, visible consent (NOT a big form/panel) */}
      <div className="absolute top-2 left-2 z-[600] w-[min(300px,62%)]" data-testid="map-search">
        <div className="flex gap-1.5">
          <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder={t("map_search_ph")} data-testid="map-search-input"
            className="flex-1 min-w-0 bg-[#0A1226]/85 border border-cyan-400/25 rounded-md px-2.5 py-1.5 text-xs text-white placeholder-white/35 focus:outline-none focus:border-cyan-400/60 backdrop-blur" />
          <button onClick={search} disabled={busy || !query.trim()} data-testid="map-search-btn"
            className="shrink-0 px-3 rounded-md border border-cyan-400/40 bg-cyan-500/20 text-cyan-100 text-xs disabled:opacity-50">
            {busy ? "…" : t("map_search_btn")}
          </button>
        </div>
        {query.trim() && (
          <div className="mt-1 text-[0.58rem] text-amber-200/75 bg-[#0A1226]/80 border border-amber-400/15 rounded px-2 py-1 backdrop-blur" data-testid="map-consent">
            {t("map_consent", { q: query.trim() })}
          </div>
        )}
        {cands.length > 1 && (
          <div className="mt-1 bg-[#0A1226]/95 border border-cyan-400/25 rounded-md overflow-hidden max-h-56 overflow-y-auto scrollbar-thin" data-testid="map-candidates">
            {cands.map((c, i) => (
              <button key={c.osm_id || i} data-testid="map-candidate"
                onClick={() => { didFly.current = true; goTo(c.lat, c.lng); setPlace(c.display_name); setCands([]); }}
                className="block w-full text-left px-2.5 py-1.5 text-[0.7rem] text-white/80 hover:bg-cyan-500/15 border-b border-white/5 last:border-0">
                {c.display_name}
              </button>
            ))}
          </div>
        )}
      </div>

      {place && <div className="absolute bottom-6 left-2 z-[600] max-w-[60%] text-xs text-cyan-200 bg-[#0A1226]/80 border border-cyan-400/30 rounded-md px-2 py-1 backdrop-blur truncate" data-testid="map-place">{place}</div>}
    </div>
  );
};

// One source row (research / news). Untrusted links only become clickable when they are http(s).
const SourceLink = ({ it, testid, big = false }) => {
  const href = safeHref(it.url);
  const Tag = href ? "a" : "div";
  const linkProps = href ? { href, target: "_blank", rel: "noopener noreferrer nofollow" } : {};
  return (
    <Tag {...linkProps} data-testid={testid} className={`block group ${big ? "border-b border-white/5 pb-2.5" : ""}`}>
      <div className={`${big ? "text-sm" : "text-xs"} text-white/90 group-hover:text-cyan-200 leading-snug`}>{it.title}</div>
      <div className={`${big ? "text-[0.68rem] mt-0.5" : "text-[0.64rem]"} text-white/45 truncate`}>{it.source}{it.published_label ? ` · ${it.published_label}` : ""}</div>
      {big && it.summary && <div className="text-xs text-white/55 mt-1 line-clamp-2">{it.summary}</div>}
    </Tag>
  );
};

/* Spotify card: shows ONLY what Spotify reports (no local "playing" flag). Controls call the server, which
   re-reads the real player state after each command. Premium / no-device errors are shown verbatim. */
/* Apple Music card: the command from the assistant is executed on the REAL MusicKit player in this browser; the
   card shows only what the player reports (or the honest error). */
const AppleMusicBody = ({ data }) => {
  const { tx } = useI18n();
  const [st, setSt] = useState({ loaded: false, connected: false });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const cmdKey = JSON.stringify(data?.command || {});
  useEffect(() => {
    let unsub = () => {};
    let alive = true;
    import("@/lib/appleMusic").then(async (m) => {
      unsub = m.subscribe((x) => alive && setSt(x));
      if (data?.command) {
        try { await m.command(data.command); setErr(null); }
        catch (e) { if (alive) setErr(e?.response?.data?.detail || e?.message || "İşlem başarısız"); }
      }
    });
    return () => { alive = false; unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmdKey]);
  const act = async (action) => {
    setBusy(true);
    try { const m = await import("@/lib/appleMusic"); await m.command({ action }); setErr(null); }
    catch (e) { setErr(e?.response?.data?.detail || e?.message || "İşlem başarısız"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5" data-testid="apple-music-widget">
      {err && <p className="text-[0.68rem] text-amber-300/80" data-testid="apple-music-error">{tx(err)}</p>}
      {!st.connected && !err && <p className="text-xs text-white/50">{tx("Apple Music bu tarayıcıda bağlı değil — çalıyormuş gibi gösterilmez.")}</p>}
      {st.track ? (
        <div className="text-xs"><div className="text-white/90 truncate">{st.track.name}</div><div className="text-white/45 truncate">{st.track.artists}</div></div>
      ) : null}
      <div className="flex items-center gap-1.5 text-[0.68rem]">
        <span className={st.is_playing ? "text-emerald-300" : "text-white/45"} data-testid="apple-music-play-state">{st.is_playing ? tx("çalıyor") : tx("duraklatıldı")}</span>
        <span className="text-white/35">· Apple Music</span>
      </div>
      {st.connected && (
        <div className="flex gap-1.5">
          {[["previous", "⏮"], [st.is_playing ? "pause" : "play", st.is_playing ? "⏸" : "▶"], ["next", "⏭"]].map(([a, icon]) => (
            <button key={a} disabled={busy} onClick={() => act(a)} aria-label={tx(a === "play" ? "Çal" : a === "pause" ? "Duraklat" : a === "next" ? "Sonraki" : "Önceki")}
              className="h-7 w-9 rounded-md border border-cyan-400/30 text-cyan-200 text-xs disabled:opacity-40" data-testid={`apple-music-${a}`}>{icon}</button>
          ))}
        </div>
      )}
    </div>
  );
};

const MusicBody = ({ data }) => {
  const { tx } = useI18n();
  const [st, setSt] = useState(data || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSt(data || {}); }, [data]);
  if (st.connected === false) return <p className="text-xs text-amber-300/80">{tx("Spotify bağlı değil. Ayarlar → Bağlantılar → Spotify'dan bağlayın — çalıyormuş gibi gösterilmez.")}</p>;
  const act = async (action) => {
    setBusy(true);
    try { const r = await api.post("/spotify/control", { action }); setSt({ connected: true, ...(r.data?.state || {}) }); }
    catch (e) { setSt((s) => ({ ...s, error: e?.response?.data?.detail || tx("İşlem başarısız") })); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5" data-testid="music-card">
      {st.error && <p className="text-[0.68rem] text-amber-300/80">{tx(st.error)}</p>}
      {st.track ? (
        <div className="text-xs"><div className="text-white/90 truncate">{st.track.name}</div><div className="text-white/45 truncate">{st.track.artists}</div></div>
      ) : <p className="text-xs text-white/50">{tx(st.note || "Etkin Spotify cihazı yok (Spotify uygulaması açık değil).")}</p>}
      <div className="flex items-center gap-1.5 text-[0.68rem]">
        <span className={st.is_playing ? "text-emerald-300" : "text-white/45"} data-testid="music-state">{st.is_playing ? tx("çalıyor") : tx("duraklatıldı")}</span>
        {st.device?.name && <span className="text-white/40 truncate">· {st.device.name}</span>}
      </div>
      <div className="flex gap-1.5">
        {[["previous", "⏮"], [st.is_playing ? "pause" : "play", st.is_playing ? "⏸" : "▶"], ["next", "⏭"]].map(([a, icon]) => (
          <button key={a} disabled={busy} onClick={() => act(a)} aria-label={tx(a === "play" ? "Çal" : a === "pause" ? "Duraklat" : a === "next" ? "Sonraki" : "Önceki")}
            className="h-7 w-9 rounded-md border border-cyan-400/30 text-cyan-200 text-xs disabled:opacity-40" data-testid={`music-${a}`}>{icon}</button>
        ))}
      </div>
    </div>
  );
};

const SmallBody = ({ kind, data }) => {
  const { t, tx } = useI18n();
  if (kind === "mail") {
    const msgs = data?.messages || [];
    if (!msgs.length) return <p className="text-xs text-white/50">{tx("Yeni özet yok.")}</p>;
    return (
      <div className="space-y-1.5">
        {typeof data.unread === "number" && <div className="text-[0.68rem] text-cyan-300">{data.unread} {tx("okunmamış")}</div>}
        {msgs.slice(0, 4).map((m, i) => (
          <div key={i} className="text-xs">
            <div className="text-white/85 truncate">{m.subject || tx("(konu yok)")}</div>
            <div className="text-white/45 truncate text-[0.68rem]">{m.from}</div>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "calendar") {
    const evs = data?.events || [];
    if (!evs.length) return <p className="text-xs text-white/50">{tx("Yaklaşan etkinlik yok.")}</p>;
    return (
      <div className="space-y-1.5">
        {evs.slice(0, 5).map((e, i) => (
          <div key={i} className="text-xs flex items-center gap-2">
            <span className="text-cyan-300 text-[0.68rem] shrink-0">{e.when}</span>
            <span className="text-white/85 truncate">{e.title}</span>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "research") {
    const items = data?.items || [];
    const note = data?.message ? <p className="text-xs text-amber-300/80" data-testid="research-note">{tx(data.message)}</p> : null;
    if (!items.length) return note || <p className="text-xs text-amber-300/80">{tx("Araştırma sonucu bulunamadı.")}</p>;
    return <div className="space-y-2">{note}{items.slice(0, 5).map((it, i) => <SourceLink key={i} it={it} testid="research-item" />)}</div>;
  }
  if (kind === "news") {
    const items = data?.items || [];
    if (!items.length) return <p className="text-xs text-amber-300/80">{t(data?.today_only ? "news_notfound_today" : "news_notfound")}. {tx("Kaynaklara ulaşılamadıysa kısa süre sonra tekrar deneyin.")}</p>;
    return <div className="space-y-2">{items.slice(0, 5).map((it, i) => <SourceLink key={i} it={it} testid="news-item" />)}</div>;
  }
  if (kind === "music") return data?.provider === "apple_music" ? <AppleMusicBody data={data} /> : <MusicBody data={data} />;
  return <p className="text-xs text-white/50">{data?.text || ""}</p>;
};

const DocBody = ({ data }) => {
  const { tx } = useI18n();
  const lines = data?.lines || [];
  return (
    <div className="text-sm text-white/85 space-y-1">
      {data?.subtitle && <div className="text-white/50 text-xs mb-1">{data.subtitle}</div>}
      {lines.map((l, i) => <div key={i} className="flex justify-between gap-3"><span className="truncate">{l.label}</span><span className="text-white/60 shrink-0">{l.value}</span></div>)}
      {data?.total && <div className="flex justify-between gap-3 pt-2 mt-2 border-t border-white/10 text-cyan-200"><span>{tx("Toplam")}</span><span>{data.total}</span></div>}
    </div>
  );
};

const NewsBody = ({ data }) => {
  const { t, tx, lang } = useI18n();
  const items = data?.items || [];
  return (
    <div className="space-y-3" data-testid="news-big">
      {data?.category && <div className="text-xs text-cyan-300/80">{tx(data.category)}{data?.today_only ? ` · ${tx("bugün")}` : ""}</div>}
      {items.length === 0 && <p className="text-sm text-amber-300/80">{t("news_notfound")}. {tx("Kaynaklara ulaşılamadıysa kısa süre sonra tekrar deneyin.")}</p>}
      {items.map((it, i) => <SourceLink key={i} it={it} testid="news-item" big />)}
      {data?.fetched_at && <div className="text-[0.6rem] text-white/30">{tx("Kaynak: yayıncı RSS akışları · çekilme:")} {new Date(data.fetched_at).toLocaleTimeString(lang === "en" ? "en-GB" : "tr-TR")}</div>}
    </div>
  );
};

export const WidgetDeck = ({ onBigChange, onMapScene }) => {
  const [cards, setCards] = useState([]);   // small cards
  const [big, setBig] = useState(null);     // one big widget (map/document/news)
  const [expanded, setExpanded] = useState(null);

  useEffect(() => onWidget(({ op, widget }) => {
    if (op === "clear") { setCards([]); setBig(null); return; }
    if (op === "close") {
      setCards((c) => c.filter((w) => w.wid !== widget.wid));
      setBig((b) => (b && b.wid === widget.wid ? null : b));
      return;
    }
    const w = { ...widget, wid: widget.wid || `w_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` };
    const isBig = w.big !== undefined ? w.big : BIG_KINDS.has(w.kind);
    if (isBig) setBig(w);
    else setCards((c) => [w, ...c.filter((x) => x.kind !== w.kind)].slice(0, 3)); // one per kind, newest first, max 3
  }), []);

  // A map is a BACKGROUND scene: the orb stays in place (smaller) instead of moving to the corner.
  useEffect(() => {
    if (onBigChange) onBigChange(!!big && big.kind !== "map");
    if (onMapScene) onMapScene(!!big && big.kind === "map");
  }, [big, onBigChange, onMapScene]);

  const closeBig = () => setBig(null);
  const closeCard = (wid) => setCards((c) => c.filter((w) => w.wid !== wid));

  return (
    <>
      {big && (big.kind === "map" ? (
        /* contextual background scene BEHIND the orb (not a separate window): styled tiles + inset grid/frame */
        <div className="absolute inset-0 z-0 map-scene" data-testid="map-scene">
          <MapWidget data={big.data} onClose={closeBig} />
          <div className="map-scene-grid" aria-hidden="true" />
          <div className="map-scene-vignette" aria-hidden="true" />
          <div className="map-scene-frame" aria-hidden="true"><i /><i /><i /><i /></div>
        </div>
      ) : (
        <div className="absolute inset-x-0 top-0 bottom-0 px-3 sm:px-6 pt-2 pb-28 z-20 flex" data-testid="widget-big">
          <div className="widget-card glass-blue w-full h-full rounded-2xl p-3 flex flex-col pointer-events-auto">
            <div className="flex items-center justify-between mb-2 shrink-0">
              <div className="flex items-center gap-2 text-sm text-cyan-200">
                {React.createElement(ICON[big.kind] || MapPin, { size: 15 })}<span className="truncate">{big.title}</span>
              </div>
              <button onClick={closeBig} data-testid="widget-big-close" className="text-white/50 hover:text-white"><X size={16} /></button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto scrollbar-thin">
              {big.kind === "news" ? <NewsBody data={big.data} /> : <DocBody data={big.data} />}
            </div>
          </div>
        </div>
      ))}

      {cards.length > 0 && (
        <div className="absolute top-14 right-3 sm:right-5 z-20 w-[min(84vw,300px)] space-y-2 pointer-events-none" data-testid="widget-cards">
          {cards.map((w) => {
            const Icon = ICON[w.kind] || Mail;
            const isExp = expanded === w.wid;
            return (
              <div key={w.wid} className={`widget-card glass-blue rounded-xl p-3 pointer-events-auto ${isExp ? "max-h-[70vh] overflow-auto scrollbar-thin" : ""}`} data-testid={`widget-card-${w.kind}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-xs text-cyan-200"><Icon size={13} /><span className="truncate">{w.title}</span></div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setExpanded(isExp ? null : w.wid)} className="text-white/40 hover:text-white" data-testid={`widget-expand-${w.kind}`}>{isExp ? <Minimize2 size={13} /> : <Maximize2 size={13} />}</button>
                    <button onClick={() => closeCard(w.wid)} className="text-white/40 hover:text-white" data-testid={`widget-close-${w.kind}`}><X size={14} /></button>
                  </div>
                </div>
                <SmallBody kind={w.kind} data={w.data} />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default WidgetDeck;
